import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'

import liffapi from '@/liffapi'

// Stored outside Pinia state — Vue's deep reactivity chokes on function closures
let _pusherUnsubscribe = null

export const useVendorStore = defineStore('vendor', {
  // Details about the current active Vendor

  state: () => ({
    entity: useStorage('vendor', {}),
    events: useStorage('vendor:events', []),
    eventsLastFetched: null,

    // Pusher state — centralized so all views react to the same source
    inboxUnreadCount: 0,
    lastConversationUpdate: null,
  }),

  getters: {
    account(state) {
      return state.entity?.account
    },

    branding(state) {
      return state.entity?.branding
    },

    logoUrl(state) {
      return state.entity?.branding?.logoUrl || state.entity?.logoUrl
    },

    displayName(state) {
      let name = state.entity?.name
      return name || 'Account'
    },

    isLoaded(state) {
      return Boolean(state.entity?.key)
    },

    key(state) {
      return state.entity?.key
    },

    slug(state) {
      return state.entity?.slug
    },

    currency(state) {
      return 'usd'
    },

    vendor(state) {
      return state.entity
    },
  },

  actions: {
    async fetchEvents(options = {}) {
      const { force = false } = options

      // Guard: vendor not loaded yet (e.g. login() still in-flight)
      if (!this.key) return this.events

      // Return cached events if available and not forcing refresh
      if (!force && this.events.length > 0 && this.eventsLastFetched) {
        const cacheAge = Date.now() - this.eventsLastFetched
        // Cache valid for 5 minutes
        if (cacheAge < 5 * 60 * 1000) {
          return this.events
        }
      }

      try {
        const response = await liffapi.events.list(this.key)
        const list = Array.isArray(response?.events)
          ? response.events
          : Array.isArray(response?.entities)
            ? response.entities
            : []

        this.events = list
        this.eventsLastFetched = Date.now()
        return list
      } catch (error) {
        // If fetch fails but we have cached data, return cache
        if (this.events.length > 0) {
          console.warn('Using cached events due to fetch failure')
          return this.events
        }
        throw error
      }
    },

    clearEventsCache() {
      this.events = []
      this.eventsLastFetched = null
    },

    async setPref(prop, value) {
      return liffapi.users.updatePrefs(this.entity.key, { [prop]: value }).then((response) => {
        Object.assign(this.entity.prefs, response.entity)
        return response
      })
    },

    clear() {
      this.entity = {}
      this.events = []
      this.eventsLastFetched = null
      this.unsubscribePusher()
    },

    setVendor(vendor) {
      if (vendor?.key && vendor.key != this.entity?.key) {
        this.clear()
      }

      this.entity = vendor

      // Start Pusher subscription for this vendor
      if (vendor?.key) {
        this.subscribeToPusher()
        this.fetchUnreadCount()
      }
    },

    async load(vendorKey = null) {
      // Load full vendor details from API
      const key = vendorKey || this.entity?.key
      if (!key) {
        console.warn('No vendor key to load')
        return null
      }

      try {
        const response = await liffapi.vendors.get(key)
        if (response?.vendor) {
          this.entity = response.vendor
          // Ensure Pusher is connected (handles reload where setVendor was skipped)
          this.subscribeToPusher()
          this.fetchUnreadCount()
          return response.vendor
        }
        return null
      } catch (error) {
        console.error('Failed to load vendor:', error)
        throw error
      }
    },

    async connectStripe() {
      // API call to get the onboarding link
      const response = await liffapi.vendors.stripeConnectStart(this.key)
      return response.url
    },

    async refreshStripeStatus() {
      const response = await liffapi.vendors.stripeConnectReturn(this.key)
      // Update local entity
      if (response.account) {
        this.entity.account = {
          ...this.entity.account,
          ...response.account,
        }
      }
      return response.account
    },

    // --- Pusher ---

    subscribeToPusher() {
      // Clean up previous subscription if switching vendors
      this.unsubscribePusher()

      if (!this.key) return

      const app = this._app
      if (!app?.config?.globalProperties?.$subscribePusher) {
        console.debug('[Pusher:store] $subscribePusher not available, skipping')
        return
      }

      const config = {
        key: import.meta.env.VITE_PUSHER_APP_KEY,
        cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
        orgId: this.key,
      }

      console.debug(`[Pusher:store] subscribing for vendor ${this.key}`)
      _pusherUnsubscribe = app.config.globalProperties.$subscribePusher(config, (event, data) => {
        console.debug(`[Pusher:store] event received: ${event}`, data?.key || '')
        if (event === 'conversation-update') {
          this.lastConversationUpdate = data
          this.fetchUnreadCount()
        }
      })
    },

    unsubscribePusher() {
      if (_pusherUnsubscribe) {
        _pusherUnsubscribe()
        _pusherUnsubscribe = null
      }
    },

    async fetchUnreadCount() {
      if (!this.key) return
      try {
        const resp = await liffapi.conversations.getUnreadCount(this.key)
        this.inboxUnreadCount = resp?.unreadCount || 0
        console.debug(`[Pusher:store] unread count: ${this.inboxUnreadCount}`)
      } catch (err) {
        console.error('[Pusher:store] failed to fetch unread count', err)
      }
    },
  },
})
